import {apiHost} from 'common/report/util';

import {apiClient} from '../../../util/api-client';
import {
  WorkspaceCreateResponse,
  WorkspaceGetPublicResponse,
  WorkspaceListResponse,
  WorkspaceTeam,
  WorkspaceMember,
  WorkspaceApiResponse,
} from './workspaces-client-types.d';

export interface EventUserPropertyFilter {
  operator: `and` | `or`;
  children: Array<object | EventUserPropertyFilter>;
}

export interface EventNameFilter {
  inclusionType: FilterInclusionType;
  filter: string[];
}

export interface WorkspaceDataFilter {
  eventNames: EventNameFilter;
  eventProperties: EventUserPropertyFilter;
  userProperties: EventUserPropertyFilter;
}

export const enum FilterDataType {
  EventNames = `event_names`,
  EventProperties = `event_properties`,
  UserProperties = `user_properties`,
}

export const enum FilterInclusionType {
  Exclude = `exclude`,
  Include = `include`,
}

export interface TeamMember {
  id: number;
  name: string;
  email: string;
  status: string;
}

export interface ProjectMember {
  id: number;
  name: string;
  email: string;
  role: string;
  targetType: string;
}

export interface WorkspaceObj {
  id: number;
  canUpdateBasic: boolean;
  canUpdateRestricted: boolean;
  canUpdateVisibility: boolean;
  createdAt: Date;
  creatorId: number;
  creator: string;
  dataFilters: WorkspaceDataFilter;
  description: string;
  hasAllOrgMembers: boolean;
  isGlobal: boolean;
  isDefault: boolean;
  isRestricted: boolean;
  isVisible: boolean;
  projectId: number;
  memberList: WorkspaceMember[];
  membershipApprovalRequired: boolean;
  name: string;
  teamList: WorkspaceTeam[];
}

export interface TeamObj extends WorkspaceTeam {
  orgId: number;
  orgRole: string;
  members: TeamMember[];
}

type WorkspaceApiObj = any;

// API is defined: app_api/projects/workspaces/views.py
class WorkspacesClient {
  static toApi(workspace: Partial<WorkspaceObj>): WorkspaceApiObj {
    const apiObj: WorkspaceApiObj = {};

    if (workspace.id) {
      apiObj.id = workspace.id;
    }
    if (workspace.projectId) {
      apiObj.project_id = workspace.projectId;
    }
    if (workspace.name) {
      apiObj.name = workspace.name;
    }
    if (workspace.dataFilters) {
      apiObj.data_filters = workspace.dataFilters;
    }
    if (workspace.description !== undefined) {
      apiObj.description = workspace.description || ``;
    }
    if (workspace.memberList) {
      apiObj.user_emails = workspace.memberList.map((m) => m.email);
    }
    if (workspace.teamList) {
      apiObj.team_ids = workspace.teamList.map((t) => t.id);
    }
    if (workspace.isDefault !== undefined) {
      apiObj.is_default = workspace.isDefault;
    }
    if (workspace.isGlobal !== undefined) {
      apiObj.is_global = workspace.isGlobal;
    }
    if (workspace.isRestricted !== undefined) {
      apiObj.is_restricted = workspace.isRestricted;
    }
    if (workspace.isVisible !== undefined) {
      apiObj.is_visible = workspace.isVisible;
    }
    return apiObj;
  }

  static toObj(apiResponse: WorkspaceApiResponse): WorkspaceObj {
    return {
      id: apiResponse.id,
      projectId: apiResponse.project_id,
      name: apiResponse.name,
      description: apiResponse.description,
      memberList: apiResponse.member_list.map<WorkspaceMember>((member) => ({
        email: member.email,
        invited: member.invited,
        name: member.name,
      })),
      dataFilters: {
        eventNames: {
          inclusionType:
            (apiResponse.data_filters.event_names.inclusion_type as FilterInclusionType) || FilterInclusionType.Include,
          filter: apiResponse.data_filters.event_names.filter || [],
        },
        userProperties: {operator: `and`, children: [], ...apiResponse.data_filters.user_properties},
        eventProperties: {operator: `and`, children: [], ...apiResponse.data_filters.event_properties},
      },
      teamList: apiResponse.team_list || [],
      createdAt: new Date(apiResponse.created_iso),
      creator: apiResponse.creator_name,
      creatorId: apiResponse.creator_id,
      isDefault: apiResponse.is_default,
      isGlobal: apiResponse.is_global,
      isRestricted: apiResponse.is_restricted,
      isVisible: apiResponse.is_visible,
      hasAllOrgMembers: apiResponse.has_all_org_members,
      membershipApprovalRequired: apiResponse.membership_approval_required,
      canUpdateBasic: apiResponse.can_update_basic,
      canUpdateRestricted: apiResponse.can_update_restricted,
      canUpdateVisibility: apiResponse.can_update_visibility,
    };
  }

  buildEndpoint({projectId, workspaceId}: {projectId: number; workspaceId?: number}) {
    if (workspaceId) {
      return `api/app/projects/${projectId}/workspaces/${workspaceId}`;
    }
    return `api/app/projects/${projectId}/workspaces`;
  }

  processResponse(response: WorkspaceListResponse | WorkspaceCreateResponse): WorkspaceObj[] {
    if (Array.isArray(response)) {
      return response.map(WorkspacesClient.toObj);
    }
    return [WorkspacesClient.toObj(response)];
  }

  async getWorkspaces({projectId}): Promise<WorkspaceObj[]> {
    const response = await apiClient.get<WorkspaceListResponse>(`${apiHost()}/${this.buildEndpoint({projectId})}`);
    return this.processResponse(response);
  }

  async getWorkspace(projectId: number, workspaceId: number): Promise<WorkspaceObj> {
    const response = await apiClient.get<WorkspaceListResponse>(
      `${apiHost()}/${this.buildEndpoint({projectId, workspaceId})}`,
    );
    return this.processResponse(response)[0];
  }

  async getPublicWorkspaces(projectId: number): Promise<WorkspaceObj[]> {
    const response = await apiClient.get<WorkspaceGetPublicResponse>(
      `${apiHost()}/${this.buildEndpoint({projectId})}/public`,
    );
    return this.processResponse(response);
  }

  async joinWorkspace(projectId: number, workspaceId: number): Promise<WorkspaceObj> {
    const response = await apiClient.post<WorkspaceApiResponse>(
      `${apiHost()}/${this.buildEndpoint({projectId, workspaceId})}/join`,
      null,
    );
    return this.processResponse(response)[0];
  }

  async updateWorkspace(projectId: number, workspaceId: number, updates: Partial<WorkspaceObj>): Promise<WorkspaceObj> {
    const response = await apiClient.patch<WorkspaceCreateResponse>(
      `${apiHost()}/${this.buildEndpoint({projectId})}/${workspaceId}`,
      {
        ...WorkspacesClient.toApi(updates),
        id: workspaceId,
      },
    );
    const [processedResponse] = this.processResponse(response);
    return processedResponse;
  }

  getAvailableMembers(projectId: number): Promise<ProjectMember[]> {
    return apiClient.get(`/api/app/projects/${projectId}/granted-users`).then((response: any) =>
      response.members.map((member) => ({
        id: parseInt(member.id, 10),
        name: member.name,
        email: member.email,
        role: member.role,
        targetType: member.target_type,
      })),
    );
  }

  getAvailableTeams(projectId: number): Promise<TeamObj[]> {
    return apiClient.get(`/api/app/projects/${projectId}/granted-teams`).then((response: any) => response.teams);
  }

  async updateMembers(projectId: number, workspaceId: number, emailList: string[]) {
    const response = await apiClient.patch<WorkspaceCreateResponse>(
      `${apiHost()}/${this.buildEndpoint({projectId})}/${workspaceId}`,
      {
        user_emails: emailList,
        id: workspaceId,
      },
    );
    const [processedResponse] = this.processResponse(response);
    return processedResponse;
  }

  async updateTeams(projectId: number, workspaceId: number, teamIds: number[]) {
    const response = await apiClient.patch<WorkspaceCreateResponse>(
      `${apiHost()}/${this.buildEndpoint({projectId})}/${workspaceId}`,
      JSON.stringify({
        team_ids: teamIds,
        id: workspaceId,
      }),
    );
    const [processedResponse] = this.processResponse(response);
    return processedResponse;
  }

  async updateDataFilters(projectId: number, workspaceId: number, dataFilters: any) {
    return this.updateWorkspace(projectId, workspaceId, {dataFilters});
  }

  async createWorkspace(
    projectId: number,
    {name, description, isRestricted = false, isVisible = false},
  ): Promise<WorkspaceObj> {
    const response = await apiClient.post<WorkspaceCreateResponse>(
      `${apiHost()}/${this.buildEndpoint({projectId})}`,
      WorkspacesClient.toApi({
        name,
        description,
        isVisible,
        isRestricted,
      }),
    );
    const [processedResponse] = this.processResponse(response);
    return processedResponse;
  }

  async deleteWorkspaces(projectId, workspaceIds: number[]): Promise<string> {
    const response = await apiClient.post<string>(`${apiHost()}/${this.buildEndpoint({projectId})}/bulk-delete`, {
      workspace_ids: workspaceIds,
    });
    return response;
  }

  async setDefaultWorkspace(projectId: number, workspaceId: number): Promise<WorkspaceObj> {
    const response = await apiClient.post<WorkspaceCreateResponse>(
      `${apiHost()}/${this.buildEndpoint({projectId, workspaceId})}/set-default`,
      {},
    );
    const [processedResponse] = this.processResponse(response);
    return processedResponse;
  }
}

export default new WorkspacesClient();
